import type { DataAttributesProps } from '../../utils/types';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { LoaderCircle } from '../../components';

const buttonVariants = cva(
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        success:
          'bg-semantic-success-foreground text-primary-foreground hover:bg-semantic-success-foreground/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/60',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        tertiary:
          'border border-primary bg-background hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        sm: 'h-9 px-4 text-sm lg:h-8 lg:px-3 lg:text-xs',
        md: 'h-10 px-6 lg:h-9 lg:px-4',
        lg: 'h-11 px-8 text-base lg:h-10 lg:text-sm',
        xl: 'h-11 px-8 text-base',
      },
      disabled: {
        false: null,
        true: 'pointer-events-none opacity-60',
      },
      isLoading: {
        false: null,
        true: 'pointer-events-none opacity-60',
      },
      isIcon: {
        false: null,
        true: 'p-0',
      },
    },
    compoundVariants: [
      {
        variant: ['outline', 'tertiary'],
        isLoading: true,
        class: 'border-input',
      },
      {
        variant: ['outline', 'tertiary'],
        disabled: true,
        class: 'border-input',
      },
      {
        isLoading: true,
        variant: ['ghost', 'secondary', 'outline', 'tertiary', 'link'],
        class: '[&_.loader-icon_svg]:text-foreground',
      },
      {
        isLoading: true,
        variant: ['default', 'destructive', 'success'],
        class: '[&_.loader-icon_svg]:text-background',
      },
      {
        variant: [
          'default',
          'destructive',
          'secondary',
          'success',
          'ghost',
          'link',
          'tertiary',
        ],
        disabled: true,
        class: 'bg-muted text-muted-foreground',
      },
      {
        isIcon: true,
        size: 'md',
        class: 'size-9',
      },
      {
        isIcon: true,
        size: 'sm',
        class: 'size-8',
      },
      {
        isIcon: true,
        size: 'lg',
        class: 'size-10',
      },
      { isIcon: true, size: 'xl', class: 'size-11' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export type ButtonProps = DataAttributesProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
    isIcon?: boolean;
    loaderOnly?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild = false,
      children,
      className,
      'data-testid': dataTestId,
      disabled,
      isLoading,
      isIcon,
      loaderOnly,
      size,
      type = 'button',
      variant,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        aria-busy={isLoading}
        className={cn(
          buttonVariants({
            variant,
            size,
            disabled,
            isLoading,
            isIcon,
          }),
          className
        )}
        disabled={disabled || isLoading}
        type={type}
        {...props}
        data-testid={dataTestId ?? type}
      >
        {!!isLoading && <LoaderCircle className="loader-icon" size="sm" />}
        {isLoading && loaderOnly ? (
          <span className="sr-only">
            <Slottable>{children}</Slottable>
          </span>
        ) : (
          <Slottable>{children}</Slottable>
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
